var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { ErrorBoundary } from '@dixa/lib-shared-habla';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useAnalyticsState } from 'analytics/components/panels/utils/useAnalyticsState/useAnalyticsState';
import { analyticsApi, } from 'apis/analytics';
import { appActions } from 'app/app.actions';
import { analytics } from 'app/utils/segment';
import { createSearchUrl } from 'app/views/layout/views/search/utils';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useShortHumanReadableTimer } from 'utils/useShortHumanReadableTimer';
import { InstrumentComparisonModal } from './components/comparison/components/modal/instrumentComparisonModal';
import { InstrumentInsightsModal } from './components/insights/instrumentInsights';
import { InstrumentModal } from './components/modal/instrumentModal';
import { InstrumentPanel } from './components/panel/instrumentPanel';
import { InstrumentRendererError } from './components/renderer/components/error/instrumentRendererError';
import { InstrumentRenderer } from './components/renderer/instrumentRenderer';
import { isTableInstrument } from './utils/groupInstruments';
import { usePersistInstrumentSettings } from './utils/usePersistInstrumentSettings';
export const AnalyticsPanel = memo(function AnalyticsPanel({ panel, }) {
    const dispatch = useAppDispatch();
    const { dashboard, context, period, filters, settings, meta: { lastRefresh, openLinksInNewTab }, } = useAnalyticsState();
    const [showInDepthModal, setShowInDepthModal] = useState(false);
    const [showCompareModal, setShowCompareModal] = useState(false);
    const [showInsightsModal, setShowInsightsModal] = useState(false);
    const [readInstrumentsQuery, { instruments = [], ai_prompts, isFetching, isError, fulfilledTimeStamp },] = analyticsApi.useLazyReadInstrumentsQuery({
        selectFromResult: (_a) => {
            var _b;
            var { data } = _a, rest = __rest(_a, ["data"]);
            return (Object.assign(Object.assign({}, rest), { instruments: data === null || data === void 0 ? void 0 : data.data.instruments, ai_prompts: (_b = data === null || data === void 0 ? void 0 : data.data.ai_prompts) !== null && _b !== void 0 ? _b : [] }));
        },
    });
    const { instrumentSettings, setInstrumentSettings } = usePersistInstrumentSettings();
    /** The relative time of the last time the instruments data was refreshed. */
    const lastRefreshTime = useShortHumanReadableTimer(fulfilledTimeStamp);
    /** Fetches the list of instruments for the panel. */
    const readInstruments = useCallback((arg) => readInstrumentsQuery({
        id: panel.id,
        context,
        period,
        filters,
        settings: Object.assign(Object.assign({}, settings), instrumentSettings),
        meta: Object.assign({}, arg === null || arg === void 0 ? void 0 : arg.meta),
    }).unwrap(), [
        context,
        filters,
        instrumentSettings,
        panel.id,
        period,
        readInstrumentsQuery,
        settings,
    ]);
    /**
     * Fetches the instrument data:
     *
     * - On initial component mount.
     * -
     * - When the `lastRefresh` timestamp changes. This timestamp is updated each
     *   time the user changes any of the global state controls (period, filters
     *   etc.), or when they manually click "Refresh data".
     * -
     * - When the `instrumentSettings` change. This happens when the user changes a
     *   setting specific to an instrument, such as chart granularity.
     */
    useEffect(() => {
        readInstruments();
        // Only run this effect when the relevant data points that should trigger an API request change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastRefresh, instrumentSettings]);
    /** Handles opening the export instrument modal. */
    const handleExport = useCallback(() => {
        dispatch(appActions.showModal({
            modal: {
                type: 'analytics:export',
                arg: {
                    instruments,
                    panel,
                },
            },
        }));
        analytics.analyticsExportClicked({
            dashboard: dashboard.title,
            panel: panel.title,
        });
    }, [dashboard.title, dispatch, instruments, panel]);
    /** Handles opening the in-depth instrument modal. */
    const handleInDepth = useCallback(() => {
        setShowInDepthModal(true);
        analytics.analyticsInDepthClicked({
            dashboard: dashboard.title,
            panel: panel.title,
        });
    }, [dashboard.title, panel.title]);
    /** Handles opening the compare instrument modal. */
    const handleCompare = useCallback(() => {
        setShowCompareModal(true);
        analytics.analyticsCompareClicked({
            dashboard: dashboard.title,
            panel: panel.title,
        });
    }, [dashboard.title, panel.title]);
    /** Handles opening the AI insights modal. */
    const handleInsights = useCallback(() => {
        setShowInsightsModal(true);
        analytics.analyticsInsightsClicked({
            dashboard: dashboard.title,
            panel: panel.title,
        });
    }, [dashboard.title, panel.title]);
    /** Handles refreshing the instrument data. */
    const handleRefresh = useCallback(() => {
        readInstruments();
        analytics.analyticsRefreshClicked({
            dashboard: dashboard.title,
            panel: panel.title,
        });
    }, [dashboard.title, panel.title, readInstruments]);
    /**
     * If the instruments contain conversations, we include an action to view them
     * in Search.
     */
    const viewConversationsAction = useMemo(() => {
        const tableInstrument = instruments.find(isTableInstrument);
        if (!tableInstrument)
            return null;
        const ids = new Set();
        const csidColumn = tableInstrument.data.columns.find(({ format }) => format === 'csid');
        if (csidColumn) {
            tableInstrument.data.rows.forEach(({ cells }) => {
                const cell = cells.find((cell) => cell.column === csidColumn.key);
                if (cell === null || cell === void 0 ? void 0 : cell.value) {
                    ids.add(cell.value.toString());
                }
            });
        }
        if (!ids.size)
            return null;
        return {
            type: 'action',
            icon: 'search',
            label: 'View conversations',
            onClick: () => {
                analytics.analyticsViewConversationsClicked({
                    dashboard: dashboard.title,
                    panel: panel.title,
                });
                const path = createSearchUrl('conversations', {
                    filters: {
                        strategy: 'all',
                        conditions: [
                            {
                                field: 'numeric_id',
                                operator: 'is_one',
                                values: Array.from(ids).map((id) => ({
                                    label: `${id}`,
                                    value: `${id}`,
                                })),
                            },
                        ],
                    },
                    sort: {
                        field: 'last_message_created_at',
                        order: 'desc',
                    },
                });
                const url = `${location.origin}${path}`;
                window.open(url, openLinksInNewTab ? '_blank' : '_self');
            },
        };
    }, [dashboard.title, instruments, openLinksInNewTab, panel.title]);
    /** The list of actions available for this instrument panel. */
    const actions = useMemo(() => {
        const conditionalActions = [];
        if (panel.exportable) {
            conditionalActions.push({
                type: 'action',
                icon: 'download',
                label: 'Export',
                onClick: handleExport,
            });
        }
        if (panel.comparable) {
            conditionalActions.push({
                type: 'action',
                icon: 'exchange',
                label: 'Compare',
                onClick: handleCompare,
            });
        }
        if (panel.has_in_depth) {
            conditionalActions.push({
                type: 'action',
                icon: 'search-plus',
                label: 'Open in-depth data',
                onClick: handleInDepth,
            });
        }
        if (panel.has_ai_insights && ai_prompts.length > 0) {
            conditionalActions.push({
                type: 'action',
                icon: 'ai',
                label: 'AI-powered insights',
                onClick: handleInsights,
            });
        }
        if (viewConversationsAction) {
            conditionalActions.push(viewConversationsAction);
        }
        return [
            ...conditionalActions,
            // Add a divider between the "Refresh" action and the conditional actions, if any exist.
            ...(conditionalActions.length > 0
                ? [{ type: 'divider' }]
                : []),
            {
                type: 'action',
                icon: 'redo',
                label: 'Refresh data',
                description: lastRefreshTime
                    ? `Last refreshed ${lastRefreshTime}`
                    : undefined,
                disabled: isFetching,
                onClick: handleRefresh,
            },
        ];
    }, [
        ai_prompts.length,
        handleCompare,
        handleExport,
        handleInDepth,
        handleInsights,
        handleRefresh,
        isFetching,
        lastRefreshTime,
        panel,
        viewConversationsAction,
    ]);
    return (_jsxs(_Fragment, { children: [_jsx(ErrorBoundary, Object.assign({ fallback: _jsx(InstrumentPanel, Object.assign({ panel: panel }, { children: _jsx(InstrumentRendererError, {}) })) }, { children: _jsx(InstrumentPanel, Object.assign({ panel: panel, actions: actions }, { children: _jsx(InstrumentRenderer, { panel: panel, instruments: instruments, isFetching: isFetching, isError: isError, onReadInstruments: readInstruments, onSetInstrumentSettings: setInstrumentSettings }) })) })), panel.has_in_depth && showInDepthModal && (_jsx(InstrumentModal, { panel: panel, onClose: () => setShowInDepthModal(false) })), panel.comparable && showCompareModal && (_jsx(InstrumentComparisonModal, { panel: panel, onClose: () => setShowCompareModal(false) })), panel.has_ai_insights && showInsightsModal && (_jsx(InstrumentInsightsModal, { panel: panel, prompts: ai_prompts, onClose: () => setShowInsightsModal(false) }))] }));
});
